// src/components/sections/section-columnist.tsx
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useState, useEffect, useMemo, useRef, memo, useCallback } from 'react';

import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import WidgetHeader from 'src/components/widgets/widget-header';
import useResponsive from 'src/utils/device';
import Carousels from 'src/components/carousels';

import CardColumnist from 'src/components/cards/card-columnist';
import CardColumn from 'src/components/cards/card-column';

type Props = {
  data: {
    columnists?: any[];
    sub?: any[];
  };
  title?: string;
  link?: string;
  showHeader?: boolean;
  defaultData: any[];
};

const AUTOPLAY_SPEED = 6000;
const FADE_DURATION = 200;

const SectionColumnist = ({ data, title = '', link = '', showHeader = true, defaultData }: Props) => {
  const router = useRouter();

  const [selectedIndex, setSelectedIndex] = useState<number>(-1); //  -1 = แสดง defaultData
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false); //  track การกด
  const responsive = useResponsive();
  const imageListWithOriginalIndex = useMemo(
    () => (data?.sub ?? []).map((item: any, originalIndex: number) => ({ item, originalIndex })).filter(({ item }) => item?.image && !isEmpty(item?.data)),
    [data?.sub]
  );

  const imageCount = imageListWithOriginalIndex.length;
  const limit = responsive?.isMobile ? 2 : 3;
  const isGridMode = imageCount > 0 && imageCount <= limit;

  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); //  timer 3 นาที
  const isPausedRef = useRef<boolean>(false);
  const imageListRef = useRef(imageListWithOriginalIndex);
  imageListRef.current = imageListWithOriginalIndex;

  const sliderRef = useRef<any>(null);

  const clearAllTimers = useCallback(() => {
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const getNextOriginalIndex = useCallback((currentOriginalIndex: number): number => {
    const list = imageListRef.current;
    if (list.length === 0) return 0;

    //  ถ้ามาจาก defaultData (-1) → ไปตัวแรก
    if (currentOriginalIndex === -1) {
      return list[0]?.originalIndex ?? 0;
    }

    const currentFilteredIdx = list.findIndex(({ originalIndex }) => originalIndex === currentOriginalIndex);
    const nextFilteredIdx = currentFilteredIdx >= list.length - 1 ? 0 : currentFilteredIdx + 1;
    return list[nextFilteredIdx]?.originalIndex ?? 0;
  }, []);

  const scheduleNext = useCallback(() => {
    clearAllTimers();

    autoplayTimerRef.current = setTimeout(() => {
      if (isPausedRef.current) {
        scheduleNext();
        return;
      }

      setIsAnimating(true);

      fadeTimerRef.current = setTimeout(() => {
        setSelectedIndex(prev => getNextOriginalIndex(prev));
        setIsAnimating(false);
        scheduleNext();
      }, FADE_DURATION);
    }, AUTOPLAY_SPEED);
  }, [clearAllTimers, getNextOriginalIndex]);

  //  Timer 3 นาที: แสดง defaultData ก่อน แล้วค่อยเริ่ม autoplay
  useEffect(() => {
    if (imageCount <= 1) return;

    const THREE_MINUTES = 3 * 60 * 1000;

    initialTimerRef.current = setTimeout(() => {
      if (!hasUserInteracted) {
        // fade ไปตัวแรก
        setIsAnimating(true);
        fadeTimerRef.current = setTimeout(() => {
          const firstOriginalIndex = imageListRef.current[0]?.originalIndex ?? 0;
          setSelectedIndex(firstOriginalIndex);
          setIsAnimating(false);

          // เริ่ม autoplay
          if (isGridMode) {
            scheduleNext();
          }
        }, FADE_DURATION);
      }
    }, THREE_MINUTES);

    return () => {
      if (initialTimerRef.current) {
        clearTimeout(initialTimerRef.current);
        initialTimerRef.current = null;
      }
    };
  }, [imageCount, isGridMode, hasUserInteracted, scheduleNext]);

  //  เริ่ม autoplay หลังจาก user กดเลือกแล้ว (selectedIndex !== -1)
  useEffect(() => {
    if (selectedIndex === -1) return; // ยังอยู่ defaultData → ไม่ autoplay
    if (isGridMode && imageCount > 1) {
      scheduleNext();
    }
    return () => clearAllTimers();
  }, [isGridMode, imageCount, selectedIndex, scheduleNext, clearAllTimers]);

  const handleColumnClick = useCallback(
    (originalIndex: number): void => {
      if (selectedIndex === originalIndex || isAnimating) return;

      //  ยกเลิก timer 3 นาที
      if (initialTimerRef.current) {
        clearTimeout(initialTimerRef.current);
        initialTimerRef.current = null;
      }
      setHasUserInteracted(true);

      clearAllTimers();
      setIsAnimating(true);

      fadeTimerRef.current = setTimeout(() => {
        setSelectedIndex(originalIndex);
        setIsAnimating(false);

        if (isGridMode && imageCount > 1) {
          scheduleNext();
        } else {
          const filteredIdx = imageListRef.current.findIndex(({ originalIndex: oi }) => oi === originalIndex);
          if (filteredIdx >= 0) {
            sliderRef.current?.slickGoTo(filteredIdx);
          }
          sliderRef.current?.slickPause();
          sliderRef.current?.slickPlay();
        }
      }, FADE_DURATION);
    },
    [selectedIndex, isAnimating, isGridMode, imageCount, clearAllTimers, scheduleNext]
  );

  const handleGridMouseEnter = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const handleGridMouseLeave = useCallback(() => {
    isPausedRef.current = false;
  }, []);

  const handleAfterChange = useCallback(
    (currentSlide: number): void => {
      const originalIndex = imageListRef.current[currentSlide]?.originalIndex ?? currentSlide;
      if (selectedIndex === originalIndex || isAnimating) return;

      //  ยกเลิก timer 3 นาที
      if (initialTimerRef.current) {
        clearTimeout(initialTimerRef.current);
        initialTimerRef.current = null;
      }
      setHasUserInteracted(true);

      setIsAnimating(true);
      setTimeout(() => {
        setSelectedIndex(originalIndex);
        setIsAnimating(false);
      }, FADE_DURATION);
    },
    [selectedIndex, isAnimating]
  );

  //  ถ้า selectedIndex === -1 ใช้ defaultData, ไม่งั้นใช้ sub data
  const currentColumnistData = useMemo(() => {
    return selectedIndex === -1 ? defaultData || [] : data?.sub?.[selectedIndex]?.data || [];
  }, [selectedIndex, defaultData, data?.sub]);

  const currentTagText = useMemo(() => {
    return selectedIndex === -1 ? title : data?.sub?.[selectedIndex]?.nameTh || title;
  }, [selectedIndex, title, data?.sub]);

  const mobileRow1 = useMemo(() => currentColumnistData.slice(0, 3), [currentColumnistData]);
  const mobileRow2 = useMemo(() => currentColumnistData.slice(3, 6), [currentColumnistData]);

  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  // หลังจาก component mount แล้วค่อย enable animation
  useEffect(() => {
    const t = setTimeout(() => setHasInitialized(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <SectionWrapper>
      {showHeader && <WidgetHeader as='h2' title={title} titleColor='#000' variant='line' position='center' showViewMore={false} viewMoreLink={link} />}

      <div className='container'>
        {/* ========== Desktop: Columnist Grid 3x2 ========== */}
        {!isEmpty(currentColumnistData) && (
          <div className={`columnist-grid isDesktop ${isAnimating ? 'fade-out' : 'fade-in'}`}>
            {currentColumnistData.slice(0, 6).map((item: any, index: number) => (
              <CardColumnist
                key={item?.id || index}
                data={item}
                showTag={true}
                tagText={currentTagText}
                type='CARD_SMALL'
                tagColor='#ed1a3b'
                eventDataLayer={{
                  type: DATALAYER_TYPE.TRACK_POSITION,
                  router: router?.pathname,
                  section: 'section-2',
                  position: `ใต้ : section-1`,
                  data: {
                    title: item?.title,
                    heading: title,
                    index: index + 1
                  }
                }}
              />
            ))}
          </div>
        )}

        {/* ========== Mobile: Columnist Scroll Rows ========== */}
        {!isEmpty(currentColumnistData) && (
          <div className={`columnist-mobile isMobile ${hasInitialized ? (isAnimating ? 'fade-out' : 'fade-in') : ''}`}>
            <div className='columnist-scroll-row'>
              {mobileRow1.map((item: any, index: number) => (
                <div key={`row1-${item?.id ?? item?.title ?? index}`} className='columnist-scroll-item'>
                  <CardColumnist
                    data={item}
                    showTag={true}
                    tagText={currentTagText}
                    tagColor='#ed1a3b'
                    type='CARD_TINY'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-2',
                      position: `ใต้ : section-1`,
                      data: {
                        title: item?.title,
                        heading: title,
                        index: index + 1
                      }
                    }}
                  />
                </div>
              ))}
            </div>

            <div className='columnist-scroll-row'>
              {mobileRow2.map((item: any, index: number) => (
                <div key={`row2-${item?.id ?? item?.title ?? index}`} className='columnist-scroll-item'>
                  <CardColumnist
                    data={item}
                    showTag={true}
                    tagText={currentTagText}
                    tagColor='#ed1a3b'
                    type='CARD_TINY'
                    eventDataLayer={{
                      type: DATALAYER_TYPE.TRACK_POSITION,
                      router: router?.pathname,
                      section: 'section-2',
                      position: `ใต้ : section-1`,
                      data: {
                        title: item?.title,
                        heading: title,
                        index: index + 1
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ========== Column Items (Carousel or Grid) ========== */}
        {imageCount > 0 && imageCount > limit ? (
          <Carousels
            infinite={true}
            ref={sliderRef}
            slideShow={4}
            mobileSlideShowTwo={2}
            pauseOnHover={false}
            autoplaySpeed={AUTOPLAY_SPEED}
            arrows={data && true}
            dots={false}
            autoplay={true}
            afterChange={handleAfterChange}
          >
            {imageListWithOriginalIndex.map(({ item, originalIndex }, filteredIndex: number) => (
              <div key={item?.id || filteredIndex} className={`column-item ${selectedIndex === originalIndex ? 'active' : ''}`} onClick={() => handleColumnClick(originalIndex)}>
                <CardColumn
                  data={item}
                  linkTo={false}
                  type='CARD_SMALL'
                  eventDataLayer={{
                    type: DATALAYER_TYPE.TRACK_POSITION,
                    router: router?.pathname,
                    section: 'section-2',
                    position: `ใต้ : section-1`,
                    data: {
                      title: item.nameTh,
                      heading: title,
                      index: `carousel ${filteredIndex + 1}`
                    }
                  }}
                />
              </div>
            ))}
          </Carousels>
        ) : (
          <div className='column-item-grid' onMouseEnter={handleGridMouseEnter} onMouseLeave={handleGridMouseLeave}>
            {imageListWithOriginalIndex.map(({ item, originalIndex }, filteredIndex: number) => (
              <div key={item?.id || filteredIndex} className={`column-item ${selectedIndex === originalIndex ? 'active' : ''}`} onClick={() => handleColumnClick(originalIndex)}>
                <CardColumn
                  data={item}
                  linkTo={false}
                  type='CARD_MEDIUM'
                  eventDataLayer={{
                    type: DATALAYER_TYPE.TRACK_POSITION,
                    router: router?.pathname,
                    section: 'section-2',
                    position: `ใต้ : section-1`,
                    data: {
                      title: item.nameTh,
                      heading: title,
                      index: `carousel ${filteredIndex + 1}`
                    }
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

const SectionWrapper = styled.section`
  padding: 30px 0;
  padding-top: 0;
  .container {
    padding: 0 15px;

    @media (max-width: 768px) {
      padding: 0 10px;
    }
  }

  /* ========== Desktop/Mobile Toggle ========== */
  .isDesktop {
    display: grid;
    @media (max-width: 768px) {
      display: none;
    }
  }

  .isMobile {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }

  /* ========== Desktop: Columnist Grid 3x2 ========== */
  .columnist-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin: 30px 0;
    min-height: 344px;

    will-change: opacity, transform;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;

    &.fade-out {
      opacity: 0;
      transform: translateY(8px);
    }

    &.fade-in {
      opacity: 1;
      transform: translateY(0);
    }

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .column-item-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, 317px);
    gap: 20px;
    margin-top: 30px;
    justify-content: center;
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, 200px);
    }
  }

  /* ========== Mobile: Columnist Scroll ========== */
  .columnist-mobile {
    margin: 20px 0;
    min-height: 244px;
    opacity: 1; /*  default visible */
    transform: none;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;

    &.fade-out {
      will-change: opacity, transform;
      opacity: 0;
      transform: translateY(8px);
    }

    &.fade-in {
      will-change: opacity, transform;
      opacity: 1;
      transform: translateY(0);
    }
  }

  .columnist-scroll-row {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    padding: 5px 0 15px;
    padding-left: 10px;
    padding-right: 10px;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }

    scroll-snap-type: x mandatory;
  }

  .columnist-scroll-item {
    flex-shrink: 0;
    width: 280px;
    /* scroll-snap-align: start; */

    @media (max-width: 400px) {
      width: 260px;
    }
  }

  /* ========== Desktop: Column Grid 4x1 ========== */
  .column-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-top: 30px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* ========== Mobile: Column Grid 2x2 ========== */
  .column-mobile {
    margin-top: 20px;
  }

  .column-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* ========== Column Item ========== */
  .column-item {
    cursor: pointer;
    border-radius: 12px;
    overflow: hidden;
    padding: 25px;

    will-change: transform;
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }

    &:active {
      transform: scale(0.98);
    }

    &.active {
      .title {
        color: #ed1a3b;
      }
    }

    @media (max-width: 768px) {
      border-radius: 10px;
      padding: 15px;
    }
  }

  @media (max-width: 768px) {
    .slick-dots {
      display: none !important;
    }
  }

  .slick-slider {
    .slick-prev,
    .slick-next {
      &:before {
        font-size: 38px;
      }
    }
    .slick-prev {
      z-index: 1;
      left: 0px;
      @media (min-width: 1300px) {
        left: -40px;
      }
    }
    .slick-next {
      z-index: 1;
      right: 7px;
      @media (min-width: 1300px) {
        right: -20px;
      }
    }
  }

  .overflow-hidden {
    overflow: unset;
  }
`;

export default memo(SectionColumnist);
